import * as React from "react"
import { router } from "@inertiajs/react"
import { Eye } from "lucide-react"
import { Button } from "@/components/ui/Button"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/Table"
import { Card } from "@/components/ui/Card"
import { Badge } from "@/components/ui/Badge"
import { Avatar } from "@/components/ui/Avatar"
import AssignRoleDialog from "./components/AssignRoleDialog"
import EditMemberDialog from "./components/EditMemberDialog"
import PendingRequestsSection, { type PendingRequest } from "./components/PendingRequestsSection"
import RolePermissionsModal, { type PermissionCatalog } from "./components/RolePermissionsModal"

type Node = { id: string; name: string; parent_id: string | null; depth: number }

type UserRole = {
  id: string
  role: { id: string; name: string; system: boolean }
  org_node: { id: string; name: string }
  granted_at: string
}

type Member = {
  user_id: string
  name: string
  email: string
  avatar_url: string | null
  slack_handle: string | null
  active: boolean
  is_self: boolean
  joined_at: string | null
  role_count: number
  pending_review: boolean
  onboarding_request: PendingRequest | null
  user_roles: UserRole[]
}

type RoleEntry = { id: string; name: string; system?: boolean; permission_keys: string[] }

type Props = {
  members: Member[]
  roles: RoleEntry[]
  permission_catalog: PermissionCatalog
  org_tree: Node[]
  can: { assign: boolean; revoke_super_admin: boolean; update: boolean; deactivate: boolean; impersonate: boolean }
}

export default function MembersIndex({ members, roles, permission_catalog, org_tree, can }: Props) {
  const [showDeactivated, setShowDeactivated] = React.useState(false)
  const [permissionsRoleId, setPermissionsRoleId] = React.useState<string | null>(null)
  const rolesById = React.useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles])
  const permissionsRole = permissionsRoleId ? rolesById.get(permissionsRoleId) : null

  const pending = members
    .filter((m) => m.active && m.pending_review && m.onboarding_request)
    .map((m) => ({ user_id: m.user_id, name: m.name, email: m.email, request: m.onboarding_request! }))

  // Unapproved self-signups live ONLY in the pending section, not the table.
  const approved = members.filter((m) => !(m.active && m.pending_review))
  const deactivatedCount = approved.filter((m) => !m.active).length
  const visibleMembers = showDeactivated ? approved : approved.filter((m) => m.active)

  const revoke = (ur: UserRole) => {
    if (!confirm(`Revoke ${ur.role.name} @ ${ur.org_node.name}?`)) return
    router.delete(`/user_roles/${ur.id}`)
  }

  return (
    <AppLayout header={<PageTitle title="Members" subtitle="People in your org" />}>
      <div className="flex flex-col gap-4">
        {can.assign && pending.length > 0 && (
          <PendingRequestsSection pending={pending} roles={roles} nodes={org_tree} />
        )}
        {deactivatedCount > 0 && (
          <div className="flex justify-end">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={showDeactivated}
                onChange={(e) => setShowDeactivated(e.target.checked)}
                className="accent-primary"
              />
              Show deactivated ({deactivatedCount})
            </label>
          </div>
        )}
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role assignments</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleMembers.map((m) => (
                <TableRow key={m.user_id} className={!m.active ? "opacity-70" : undefined}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar size="md" name={m.name} avatarUrl={m.avatar_url} className="border border-border" />
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          {can.update ? (
                            <EditMemberDialog
                              member={{
                                user_id: m.user_id,
                                name: m.name,
                                slack_handle: m.slack_handle,
                                active: m.active,
                              }}
                              canDeactivate={can.deactivate}
                              isSelf={m.is_self}
                            />
                          ) : (
                            <span>{m.name}</span>
                          )}
                          {!m.active && <Badge variant="secondary">Inactive</Badge>}
                        </div>
                        {m.slack_handle && (
                          <span className="text-xs text-muted-foreground">@{m.slack_handle}</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>
                    {m.user_roles.length === 0 ? (
                      <span className="text-muted-foreground text-sm">None</span>
                    ) : (
                      <ul className="flex flex-col gap-0.5 text-sm">
                        {m.user_roles.map((ur) => (
                          <li key={ur.id} className="flex items-center gap-2">
                            <span>
                              <button
                                type="button"
                                onClick={() => setPermissionsRoleId(ur.role.id)}
                                className="cursor-pointer hover:underline"
                                title="View permissions"
                              >
                                {ur.role.name}
                              </button>{" "}
                              <span className="text-muted-foreground">@ {ur.org_node.name}</span>
                            </span>
                            {(ur.role.system ? can.revoke_super_admin : can.assign) && (
                              <button
                                type="button"
                                onClick={() => revoke(ur)}
                                className="text-xs text-destructive hover:underline cursor-pointer"
                              >
                                Revoke
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </TableCell>
                  <TableCell>{m.joined_at?.slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    {can.impersonate && m.active && !m.is_self && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground"
                        onClick={() => router.post(`/team/members/${m.user_id}/impersonate`)}
                      >
                        <Eye size={13} />
                        View as
                      </Button>
                    )}
                    {can.assign && m.active && (
                      <AssignRoleDialog
                        userId={m.user_id}
                        roles={roles}
                        nodes={org_tree}
                        existing={m.user_roles.map((ur) => ({
                          role_id: ur.role.id,
                          node_id: ur.org_node.id,
                        }))}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        {permissionsRole && (
          <RolePermissionsModal
            role={{
              name: permissionsRole.name,
              system: permissionsRole.system ?? false,
              permission_keys: permissionsRole.permission_keys,
            }}
            catalog={permission_catalog}
            open
            onClose={() => setPermissionsRoleId(null)}
          />
        )}
      </div>
    </AppLayout>
  )
}
